import React, { memo, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { InterPoint } from '@saviynt/common';
import {
  Box,
  ButtonIcon,
  Chip,
  type FloatingPlacements,
  Icon,
  InlineMessage,
  InputField,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';
import { addDays, addYears, format } from 'date-fns';

import {
  CF_NEU_BLD,
  CF_NEU_INV,
  CF_NEU_STL,
  CF_SEC_MED,
  MIN_COMMENT_LENGTH,
  testIds,
} from '../../../constants';
import { DATE_FILTER_OPTIONS, MAX_ACCESS_COUNT } from '../constants';
import { ListDateFilter, SoxCritical } from '../Shared';
import {
  AccessTypeMap,
  type EnterpriseRoleAccessData,
  type EntitlementAccessData,
} from '../types';

import { type TrackedListItem } from '.';

interface AccessModalListItemProps {
  isFullView: boolean;
  assignedCount: number | null;
  isDisabled: boolean;
  isAssignedList: boolean;
  listItem: TrackedListItem;
  handleClick: (id: number, type: AccessTypeMap, config: object) => void;
  handleRowClick: (id: number, type: AccessTypeMap) => void;
  handleOptionClick: (
    id: number,
    type: AccessTypeMap,
    startDate?: Date | null,
    enddate?: Date | null
  ) => void;
  handleComment: (id: number, type: AccessTypeMap, text: string) => void;
}

/**
 * AccessModalListItem
 */
function AccessModalListItem({
  isFullView,
  assignedCount,
  isDisabled,
  isAssignedList,
  listItem,
  handleClick,
  handleRowClick,
  handleOptionClick,
  handleComment,
}: AccessModalListItemProps) {
  const {
    tracker: {
      id,
      isChecked,
      type,
      isStartDateCritical,
      isEndDateCritical,
      currentStartDate,
      currentEndDate,
      allowedEndDateRange,
      isStartDateDisabled,
      isJustificationRequired,
      currentJustification,
      isJustificationCritical,
    },
    ...listData
  } = listItem;

  const isLimit = Boolean(
    assignedCount !== null &&
      MAX_ACCESS_COUNT !== null &&
      assignedCount >= MAX_ACCESS_COUNT
  );
  const isRecommended = Boolean(
    listData.recommended === true || listData.recommended === 'true'
  );
  const maxDate =
    allowedEndDateRange && currentStartDate
      ? addDays(
          new Date(currentStartDate),
          Math.ceil(Number(allowedEndDateRange) / 24)
        )
      : addYears(new Date(), 9999);

  const isItemDisabled = useMemo(() => {
    if ((isLimit && !isChecked) || isDisabled) {
      return true;
    }

    return false;
  }, [isChecked, isLimit, isDisabled]);
  const { chipDateText, isBothDates, isAnyDate } = useMemo(() => {
    if (!currentStartDate && !currentEndDate) {
      return { chipDateText: null, isBothDates: false, isAnyDate: false };
    }

    const labels = {
      start: '',
      end: '',
    };

    if (currentStartDate) {
      const startLabel = format(currentStartDate, 'MMM dd, yyyy');

      labels.start = startLabel;
    }

    if (currentEndDate) {
      const endLabel = format(currentEndDate, 'MMM dd, yyyy');

      labels.end = endLabel;
    }

    return {
      chipDateText: `${labels.start} - ${labels.end}`,
      isBothDates: Boolean(currentStartDate && currentEndDate),
      isAnyDate: Boolean(currentStartDate ?? currentEndDate),
    };
  }, [currentEndDate, currentStartDate]);
  const tooltipConfigs = useMemo(
    () => ({
      text:
        listData.description && listData.description.trim().length > 0
          ? listData.description
          : '-',
      placement: 'bottom' as FloatingPlacements,
    }),
    [listData.description]
  );
  const dateCriticalText = useMemo(() => {
    if (isStartDateCritical && isEndDateCritical) {
      return 'Start Date and End Date is required';
    }

    if (isStartDateCritical) {
      return 'Start Date is required';
    }

    if (isEndDateCritical) {
      return 'End Date is required';
    }

    return null;
  }, [isEndDateCritical, isStartDateCritical]);

  // Utils
  const getToolTipText = () => {
    if (isItemDisabled) {
      return 'Max number of access has been reached. Remove one from the Assigned list before adding more.';
    }

    if (isAssignedList || isChecked) {
      return 'Remove';
    }

    return 'Add';
  };

  const handleRowPanel = useCallback(() => {
    handleRowClick(id, type);
  }, [handleRowClick, id, type]);

  return (
    <Box
      tag='div'
      className={cx(
        'AccessModalListItem-wrapper',
        (Boolean(isStartDateCritical) ||
          Boolean(isEndDateCritical) ||
          Boolean(isJustificationCritical)) &&
          'AccessModalListItem-wrapper--isCritical'
      )}>
      <button
        type='button'
        aria-disabled='false'
        tabIndex={isItemDisabled ? -1 : 0}
        onClick={() => {
          if (isItemDisabled) {
            return;
          }

          if (isFullView) {
            handleRowPanel();

            return;
          }

          handleClick(id, type, {
            ...(type === AccessTypeMap.entitlement
              ? {
                  entitlementType: (listData as EntitlementAccessData[number])
                    .entitlementtype,
                }
              : {}),
          });
        }}
        onKeyDown={(e) => {
          if (e.key !== 'Enter' || e.detail !== 1 || isItemDisabled) {
            return;
          }

          if (isFullView) {
            handleRowPanel();

            return;
          }

          handleClick(id, type, {
            ...(type === AccessTypeMap.entitlement
              ? {
                  entitlementType: (listData as EntitlementAccessData[number])
                    .entitlementtype,
                }
              : {}),
          });
        }}
        className={cx(
          'AccessModalListItem',
          isChecked && 'AccessModalListItem--isChecked',
          isAssignedList && 'AccessModalListItem--isAssignedList'
        )}>
        {isRecommended && (
          <Box tag='div' className='AccessModalListItem-starWrapper'>
            <Icon kind='StarFilled' size='smallMedium' color={CF_NEU_INV} />
          </Box>
        )}
        <Box tag='div' className='AccessModalListItem-iconWrapper'>
          {type === AccessTypeMap.entitlement && (
            <Icon kind='Entitlement' size='large' />
          )}
          {type === AccessTypeMap.enterpriseRole && (
            <Icon kind='EnterpriseRole' size='large' />
          )}
          {type === AccessTypeMap.applicationRole && (
            <Icon kind='ApplicationRole' size='large' />
          )}
        </Box>
        <Box tag='div' className='AccessModalListItem-contentWrapper'>
          <Typography kind='body2' color={CF_SEC_MED}>
            {type === AccessTypeMap.entitlement && (
              <FormattedMessage
                id='T3P1a+'
                defaultMessage='Entitlement'
                description='Entitlement'
              />
            )}
            {type === AccessTypeMap.enterpriseRole && (
              <FormattedMessage
                id='v2OB6N'
                defaultMessage='Enterprise Role'
                description='Enterprise Role'
              />
            )}
            {type === AccessTypeMap.applicationRole && (
              <FormattedMessage
                id='eMNJYH'
                defaultMessage='Application Role'
                description='Application Role'
              />
            )}
          </Typography>
          <Box tag='div' className='AccessModalListItem-midContent'>
            <Typography kind='h3' color={CF_NEU_BLD} lineLimit={1}>
              {/* eslint-disable-next-line no-nested-ternary */}
              {listData.displayname && listData.displayname.trim().length > 0
                ? listData.displayname
                : type === AccessTypeMap.entitlement
                ? (listData as EntitlementAccessData[number]).entitlement_value
                : (listData as EnterpriseRoleAccessData[number]).rolename}
            </Typography>
            <SoxCritical
              value={listData.soxcritical}
              showForList
              isInterpoint
            />
            {isAnyDate && isFullView && isAssignedList && (
              <>
                <InterPoint />
                <Chip
                  size='xSmall'
                  kind='outlined'
                  label={chipDateText}
                  color='neutral'
                  leftIcon={<Icon kind='Calendar' />}
                  isInteractionDisabled
                />
              </>
            )}
            {dateCriticalText !== null && isFullView && (
              <>
                <InterPoint />
                <InlineMessage
                  text={dateCriticalText}
                  colorTheme='critical'
                  size='medium'
                  className='AccessModalListItem-dateMessage'
                />
              </>
            )}
          </Box>
          <Box tag='div' className='AccessModalListItem-lastContent'>
            {type === AccessTypeMap.entitlement ? (
              <>
                <Typography kind='body2' color={CF_NEU_STL}>
                  <FormattedMessage
                    id='JxCLGu'
                    defaultMessage='Type:'
                    description='Entitlement type'
                  />
                </Typography>
                <Typography kind='body2' color={CF_NEU_BLD} isNoWrap>
                  {(listData as EntitlementAccessData[number]).entitlementtype}
                </Typography>
                {listData.description &&
                  listData.description.trim().length > 0 && (
                    <>
                      <InterPoint />{' '}
                      <Typography
                        kind='body2'
                        color={CF_NEU_STL}
                        lineLimit={1}
                        tooltipConfigs={tooltipConfigs}
                        showTooltipOnHover>
                        {listData.description}
                      </Typography>
                    </>
                  )}
              </>
            ) : (
              <Typography
                kind='body2'
                color={CF_NEU_STL}
                lineLimit={1}
                tooltipConfigs={tooltipConfigs}
                showTooltipOnHover
                className='AccessModalListItem-lastContentDescription'>
                {listData.description &&
                listData.description.trim().length > 0 ? (
                  listData.description
                ) : (
                  <FormattedMessage
                    id='YoUJI1'
                    defaultMessage='-'
                    description='Hyphen'
                  />
                )}
              </Typography>
            )}
          </Box>
        </Box>
        <Box
          tag='div'
          className={cx(
            'AccessModalListItem-selectWrapper',
            isFullView && 'AccessModalListItem-selectWrapper--isFullView',
            isRecommended && 'AccessModalListItem-selectWrapper--isRecommended'
          )}>
          <Tooltip
            trigger={
              <ButtonIcon
                kind='outlinedSecondary'
                isSelected={isChecked && !isAssignedList}
                isDisabled={isItemDisabled}
                icon={
                  <Icon
                    kind={isAssignedList || isChecked ? 'Subtract' : 'Add'}
                    size='xSmall'
                  />
                }
                ariaLabel={isAssignedList || isChecked ? 'Remove' : 'Add'}
                onClick={(e: React.MouseEvent) => {
                  if (isFullView) {
                    e.stopPropagation();

                    if (!isItemDisabled) {
                      handleClick(id, type, {
                        ...(type === AccessTypeMap.entitlement
                          ? {
                              entitlementType: (
                                listData as EntitlementAccessData[number]
                              ).entitlementtype,
                            }
                          : {}),
                      });
                    }
                  }
                }}
                tabIndex={isFullView ? 0 : -1}
                dataTestId={`${testIds.LIST_VIEWER_ACCESS_ITEM_BTN}-${id}-${type}`}
              />
            }
            text={getToolTipText()}
            placement='left'
          />
          {isAssignedList && (
            <ListDateFilter
              options={DATE_FILTER_OPTIONS}
              trigger={
                <ButtonIcon
                  kind='ghost'
                  isDisabled={isItemDisabled}
                  icon={
                    <Icon
                      kind={isBothDates ? 'ChangeEndDate' : 'Calendar'}
                      size='xSmall'
                    />
                  }
                  onClick={(e: React.MouseEvent) => {
                    if (isFullView) {
                      e.stopPropagation();
                    }
                  }}
                  ariaLabel='End date'
                />
              }
              maxDate={maxDate}
              currentStartDate={currentStartDate}
              currentEndDate={currentEndDate}
              handleOptionsClick={(
                startDate?: Date | null,
                enddate?: Date | null
              ) => {
                handleOptionClick(id, type, startDate, enddate);
              }}
              isStartDateDisabled={Boolean(isStartDateDisabled)}
            />
          )}
        </Box>
      </button>
      {isAssignedList && isJustificationRequired && (
        <Box tag='div' className='AccessModalListItem-justificationWrapper'>
          <InputField
            name='basic'
            placeholder='Provide a reason for your decision'
            label={
              <Typography kind='h4' color={CF_NEU_BLD}>
                <FormattedMessage
                  id='I97Zxk'
                  defaultMessage='Comments'
                  description='Comments'
                />
              </Typography>
            }
            value={currentJustification}
            setValue={(text: string) => handleComment(id, type, text)}
            kind='multiline'
            isRequired
            isCritical={isJustificationCritical}
            CriticalHelperText={
              <InlineMessage
                text={
                  currentJustification.trim().length < 1
                    ? 'This required field is incomplete'
                    : `Your comments must contain more than ${
                        MIN_COMMENT_LENGTH - 1
                      } characters.`
                }
                colorTheme='critical'
                size='small'
                leftIcon={<Icon kind='AlertCriticalFilled' />}
              />
            }
          />
        </Box>
      )}
    </Box>
  );
}

export default memo(AccessModalListItem);
